import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { MessageDescriptor, defineMessages, useIntl } from 'react-intl';

import crest from 'assets/user/crest.png';
import { FontAwesomeIcon, IconDefinition } from 'atoms/icons';
import { rivals } from 'lib/glossary';
import { Color } from 'style/types';

import {
  EllipsisLabel,
  IconWrapper,
  Label,
  PictureWrapper,
  Root,
  Skeleton,
} from '../ui';

type StatusType =
  | 'pending-received'
  | 'pending-sent'
  | 'accepted'
  | 'rejected'
  | 'expired'
  | 'live'
  | 'win'
  | 'loss'
  | 'draw'
  | 'lineup-set'
  | 'lineup-not-set'
  | 'unmatched';

const messages = defineMessages({
  requested: {
    defaultMessage: 'Requested',
    id: 'rivals.MatchupStatus.received.pending',
  },
  requesting: {
    defaultMessage: 'Requesting',
    id: 'rivals.MatchupStatus.sent.pending',
  },
  unmatched: {
    defaultMessage: 'Unmatched',
    id: 'rivals.MatchupStatus.rejected',
  },
  expired: {
    defaultMessage: 'Expired',
    id: 'rivals.MatchupStatus.expired',
  },
  live: {
    defaultMessage: 'Live',
    id: 'rivals.MatchupStatus.live',
  },
  win: {
    defaultMessage: 'Win',
    id: 'rivals.MatchupStatus.win',
  },
  loss: {
    defaultMessage: 'Loss',
    id: 'rivals.MatchupStatus.loss',
  },
  draw: {
    defaultMessage: 'Draw',
    id: 'rivals.MatchupStatus.draw',
  },
});

const Item = ({
  pictureUrl,
  displayName,
  color,
  message,
  gradientBorder,
  onClick,
  icon,
  loading,
}: {
  pictureUrl: string | null;
  displayName: string;
  color: Color;
  message: MessageDescriptor;
  gradientBorder?: boolean;
  onClick?: () => void;
  icon?: IconDefinition;
  loading?: boolean;
}) => {
  const { formatMessage } = useIntl();
  return (
    <Root
      as={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
    >
      <PictureWrapper className={classNames({ gradientBorder })}>
        <img src={pictureUrl || crest} alt="" />
        {icon && (
          <IconWrapper>
            <FontAwesomeIcon icon={icon} />
          </IconWrapper>
        )}
      </PictureWrapper>
      {loading ? (
        <Skeleton />
      ) : (
        <div>
          <EllipsisLabel>{displayName}</EllipsisLabel>
          <Label color={color}>{formatMessage(message)}</Label>
        </div>
      )}
    </Root>
  );
};

type Props = {
  status: StatusType;
  pictureUrl: string | null;
  displayName: string;
  onClick?: () => void | Promise<void>;
  loading?: boolean;
};
export const MatchupItem = ({
  status,
  pictureUrl,
  displayName,
  onClick,
  loading,
}: Props) => {
  switch (status) {
    case 'pending-received':
      return (
        <Item
          pictureUrl={pictureUrl}
          displayName={displayName}
          color="var(--c-rivals-friendlies)"
          message={messages.requested}
          onClick={onClick}
          gradientBorder
          loading={loading}
        />
      );

    case 'pending-sent':
      return (
        <Item
          pictureUrl={pictureUrl}
          displayName={displayName}
          color="rgba(var(--c-rgb-rivals-friendlies), 0.5)"
          onClick={onClick}
          message={messages.requesting}
          loading={loading}
        />
      );

    case 'accepted':
      return (
        <Item
          pictureUrl={pictureUrl}
          displayName={displayName}
          color="var(--c-rivals-friendlies)"
          message={rivals.matchedUp}
          onClick={onClick}
          loading={loading}
        />
      );

    case 'unmatched':
      return (
        <Item
          pictureUrl={pictureUrl}
          displayName={displayName}
          color="var(--c-neutral-500)"
          message={messages.unmatched}
          onClick={onClick}
          icon={faPlus}
          loading={loading}
        />
      );

    case 'expired':
      return (
        <Item
          pictureUrl={pictureUrl}
          displayName={displayName}
          color="rgba(var(--c-rgb-rivals-friendlies), 0.5)"
          message={messages.expired}
          onClick={onClick}
        />
      );

    case 'live':
      return (
        <Item
          pictureUrl={pictureUrl}
          displayName={displayName}
          color="var(--c-rivals-friendlies)"
          message={messages.live}
          onClick={onClick}
        />
      );

    case 'win':
      return (
        <Item
          pictureUrl={pictureUrl}
          displayName={displayName}
          color="var(--c-green-600)"
          message={messages.win}
          onClick={onClick}
        />
      );

    case 'draw':
      return (
        <Item
          pictureUrl={pictureUrl}
          displayName={displayName}
          color="var(--c-brand-300)"
          message={messages.draw}
          onClick={onClick}
        />
      );

    case 'loss':
      return (
        <Item
          pictureUrl={pictureUrl}
          displayName={displayName}
          color="var(--c-red-600)"
          message={messages.loss}
          onClick={onClick}
        />
      );

    case 'lineup-set':
      return (
        <Item
          pictureUrl={pictureUrl}
          displayName={displayName}
          color="var(--c-green-600)"
          message={rivals.lineupSet}
          onClick={onClick}
        />
      );
    case 'lineup-not-set':
      return (
        <Item
          pictureUrl={pictureUrl}
          displayName={displayName}
          color="var(--c-red-600)"
          message={rivals.lineupNotSet}
          onClick={onClick}
        />
      );

    default:
      return null;
  }
};
